import { Grid } from "@mui/material";
import SpikeCharts from "../SpikeCharts";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import PasswordShow from "../../../Common/passwordShow";
import {
  dateInYYYYMMDD,
  getDefaultDateRange,
  getLastRangeStartDate,
  getMinMaxValues,
  getPercentValue,
  getStartDateAndEndDate,
  getXAxisData,
  getYAxisTicks,
} from "../../../Constants/utils";
import { useAuthDetails } from "../../../Common/cookiesHelper";

export const CommonChart = ({
  componentRef,
  activeType,
  formatFunction,
  presentDate,
  sliceCallback,
  yAxisText,
  reduxState,
  reduxStateKey,
  reduxStateTotalKey,
  valueKey,
  chartKey,
  merchantId,
  chartInView,
  title,
  totalValueFunction,
  sliceCallbackRunTimeData,
  sliceCallDiscountPercentageData,
  selectedOutletIds,
}) => {
  const { handleCoockieExpire, getUnAutherisedTokenMessage } = PasswordShow();
  const dispatch = useDispatch();
  const { LoginGetDashBoardRecordJson } = useAuthDetails();
  const isViewingCurrentPeriod = (() => {
    const d = presentDate instanceof Date ? presentDate : new Date(presentDate);
    const today = new Date();

    if (activeType === "Day") {
      return (
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
      );
    }

    if (activeType === "Week") {
      const getWeekStartMonday = (date) => {
        const x = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const day = x.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        x.setDate(x.getDate() + diff);
        x.setHours(0, 0, 0, 0);
        return x;
      };
      const weekStart = getWeekStartMonday(d);
      const currentWeekStart = getWeekStartMonday(today);
      return weekStart.getTime() === currentWeekStart.getTime();
    }

    if (activeType === "Month") {
      return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
    }

    return true;
  })();
  const [xAxisDates, setXAxisDates] = useState([]);
  const [chartInViewState, setChartInViewState] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const outletCacheKey = Array.isArray(selectedOutletIds)
    ? selectedOutletIds.map(String).slice().sort().join(",") || "all"
    : "all";

  const defaultObj = {
    percent: null,
    xAxisData: [],
    minValue: 0,
    maxValue: 0,
    yAxisOptions: [],
    type: "number",
    totalValue: totalValueFunction(0),
  };
  const [chartData, setChartData] = useState(defaultObj);

  // forming chart data from redux state
  useEffect(() => {
    const bool =
      reduxState &&
      reduxState?.[reduxStateKey] &&
      reduxState?.[reduxStateKey].length > 3 &&
      !reduxState.loading;

    if (bool) {
      const getValue = (data, index) => {
        const value =
          data &&
          data[reduxStateKey] &&
          data[reduxStateKey].length > 2 &&
          data[reduxStateKey][data[reduxStateKey].length - index];
        return value?.[valueKey] && parseFloat(value?.[valueKey])
          ? parseFloat(value?.[valueKey]).toFixed(2)
          : 0;
      };

      // for Percentage variance --------------------------------------
      const oldValue = getValue(reduxState, 2);
      const newValue = getValue(reduxState, 1);
      const percent = getPercentValue(oldValue, newValue);

      // for X Axis data ----------------------------------------------
      const dataOfXAxis = reduxState?.[reduxStateKey].map((item, index) => ({
        name: xAxisDates[index],
        uv: item?.[valueKey]
          ? parseFloat(parseFloat(item?.[valueKey]).toFixed(2))
          : 0,
      }));

      // for Y Axis Data ----------------------------------------------
      const minAndMaxValue =
        dataOfXAxis && dataOfXAxis.length > 0 && getMinMaxValues(dataOfXAxis);

      const temp = getYAxisTicks(minAndMaxValue.maxUv || 0);
      const yAxisOptions = temp.map((val) => parseFloat(val));

      const amt =
        reduxState[reduxStateTotalKey] &&
          !isNaN(reduxState[reduxStateTotalKey])
          ? parseFloat(parseFloat(reduxState[reduxStateTotalKey]).toFixed(2))
          : 0;
      const totalValue = totalValueFunction(amt);

      const obj = {
        percent: parseFloat(percent)?.toFixed(2),
        xAxisData: dataOfXAxis,
        minValue: minAndMaxValue.minUv,
        maxValue: minAndMaxValue.maxUv,
        yAxisOptions,
        type: "number",
        totalValue,
      };

      setChartData(() => obj);
    }
  }, [reduxState, xAxisDates]);

  // checking if chart is in view
  useEffect(() => {
    if (chartInView && !chartInViewState) {
      setChartInViewState(() => true);
    }
  }, [chartInView, chartInViewState]);

  // setting date range
  useEffect(() => {
    const dates = getDefaultDateRange(activeType, presentDate);
    setChartInViewState(() => false);

    if (!dates || !dates.date_range) return;
    const data = getXAxisData(activeType, dates.date_range);
    setXAxisDates(data);
  }, [activeType, presentDate]);

  // fetch chart data api
  const fetchChartData = async () => {
    try {
      setRefreshLoading(true);
      const dateRange = getDefaultDateRange(activeType, presentDate);
      if (dateRange && dateRange?.date_range && merchantId && reduxState) {
        const { endDate } = getStartDateAndEndDate(dateRange?.date_range);

        const baseData = {
          merchant_id: merchantId,
          start_date: getLastRangeStartDate(dateRange?.date_range),
          end_date: endDate,
          date_range: dateRange?.date_range,
          activeType,
          date_type: activeType,
          isViewingCurrentPeriod,
          login_employee_id: LoginGetDashBoardRecordJson?.employee_id
            ? LoginGetDashBoardRecordJson?.employee_id
            : null,
        };

        const data =
          chartKey === "revenueChart" ||
            chartKey === "avgSaleValueChart" ||
            chartKey === "avgItemSaleChart" ||
            chartKey === "salesCountChart" ||
            chartKey === "customerCountChart" ||
            chartKey === "grossProfitChart" ||
            chartKey === "discountChart" ||
            chartKey === "percentageDiscountChart"
            ? {
              ...baseData,
              outlet_ids:
                Array.isArray(selectedOutletIds) && selectedOutletIds.length
                  ? selectedOutletIds.join(",")
                  : "all",
            }
            : baseData;

        await dispatch(sliceCallback(data)).unwrap();
      }
    } catch (error) {
      if (error?.status === 401 || error?.response?.status === 401) {
        getUnAutherisedTokenMessage();
        handleCoockieExpire();
      } else if (error?.status === "Network Error") {
        // getNetworkError();
      }
    } finally {
      setRefreshLoading(false);
    }
  };
  // fetch chart data api
  const fetchRunTimeData = async () => {
    try {
      setRefreshLoading(true);
      const dateRange = getDefaultDateRange(activeType, presentDate);
      if (dateRange && dateRange?.date_range && merchantId && reduxState) {
        const { endDate } = getStartDateAndEndDate(dateRange?.date_range);

        const data = {
          merchant_id: merchantId,
          start_date: getLastRangeStartDate(dateRange?.date_range),
          end_date: endDate,
          date_range: dateRange?.date_range,
          activeType,
          date_type: activeType,
          login_employee_id: LoginGetDashBoardRecordJson?.employee_id
            ? LoginGetDashBoardRecordJson?.employee_id
            : null,
        };
        await dispatch(sliceCallbackRunTimeData(data)).unwrap();
      }
    } catch (error) {
      if (error?.status === 401 || error?.response?.status === 401) {
        getUnAutherisedTokenMessage();
        handleCoockieExpire();
      } else if (error?.status === "Network Error") {
        // getNetworkError();
      }
    } finally {
      setRefreshLoading(false);
    }
  };

  // fetch chart data api
  const fetchDiscountPercentageData = async () => {
    try {
      setRefreshLoading(true);
      const dateRange = getDefaultDateRange(activeType, presentDate);
      if (dateRange && dateRange?.date_range && merchantId && reduxState) {
        const { endDate } = getStartDateAndEndDate(dateRange?.date_range);

        const data = {
          merchant_id: merchantId,
          start_date: getLastRangeStartDate(dateRange?.date_range),
          end_date: endDate,
          date_range: dateRange?.date_range,
          activeType,
          date_type: activeType,
          login_employee_id: LoginGetDashBoardRecordJson?.employee_id
            ? LoginGetDashBoardRecordJson?.employee_id
            : null,
        };
        await dispatch(sliceCallDiscountPercentageData(data)).unwrap();
      }
    } catch (error) {
      if (error?.status === 401 || error?.response?.status === 401) {
        getUnAutherisedTokenMessage();
        handleCoockieExpire();
      } else if (error?.status === "Network Error") {
        // getNetworkError();
      }
    } finally {
      setRefreshLoading(false);
    }
  };

  // fetching chart data from api
  useEffect(() => {
    if (merchantId && chartInViewState) {
      fetchChartData();
    }
  }, [
    merchantId,
    chartInViewState,
    presentDate,
    activeType,
    chartKey,
    outletCacheKey,
  ]);

  return (
    <>
      <Grid item xs={12} md={6} lg={6} ref={componentRef}>
        <SpikeCharts
          title={title}
          growth={chartData.percent}
          mainOutlet={chartData.totalValue}
          amount={chartData.totalValue}
          activeType={activeType}
          xAxisData={chartData.xAxisData}
          maxValue={chartData.maxValue}
          minValue={chartData.minValue}
          yAxisOptions={chartData.yAxisOptions}
          type={chartData.type}
          formatFunction={formatFunction}
          // loading={
          //   chartData.yAxisOptions.length > 0
          //     ? false
          //     : reduxState?.loading || reduxState?.isCancel
          // }
          loading={reduxState?.loading || reduxState?.isCancel}
          presentDate={presentDate}
          updateRunTimeDataAPI={fetchRunTimeData}
          updatedDiscountPercentageDataAPI={fetchDiscountPercentageData}
          refreshAPI={fetchChartData}
          refreshLoading={refreshLoading}
          yAxisText={yAxisText}
          selectedOutletIds={selectedOutletIds}
        />
      </Grid>
    </>
  );
};
